import { useState, useEffect, useCallback, useRef } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import styles from './Navbar.module.scss';
import { Link, useLocation } from 'react-router-dom';

type NavbarComponentProps = {
  menuItems: MenuItem[];
};

function NavbarComponent({ menuItems }: NavbarComponentProps) {
  const [isSticky, setIsSticky] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeLink, setActiveLink] = useState<string>('');
  const [showHeader, setShowHeader] = useState(false);
  const location = useLocation();

  const dropdownRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const updateActiveLink = useCallback((linkSectionMap: Map<Element, Element>) => {
    const navHeight = document.querySelector('nav')?.offsetHeight || 0;
    const currentPath = window.location.pathname;
    const isHomePage = ['/', '/index', '/index.html', '/#'].includes(currentPath);

    let newActiveLink: string | null = null;
    let maxVisible = 0;

    if (isHomePage) {
      linkSectionMap.forEach((section, link) => {
        const rect = section.getBoundingClientRect();
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, navHeight);

        if (visibleHeight > maxVisible && visibleHeight > 0) {
          maxVisible = visibleHeight;
          newActiveLink = link.getAttribute('href') ?? '/#';
        }
      });
    } else {
      newActiveLink = currentPath;
    }

    setActiveLink(newActiveLink || '/#');
  }, []);

  const handleScroll = useCallback(() => {
    const banner = document.querySelector("#banner");
    const errorPage = document.querySelector("#error-page-message");
    const isErrorPage = !!errorPage;

    setShowHeader(isErrorPage && windowWidth >= 992);

    if (banner instanceof HTMLElement) {
      const stickyPosition = banner.offsetTop;
      if (window.scrollY >= stickyPosition + 40 || windowWidth < 992 || (!banner && !showHeader)) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    } else {
      setIsSticky(!isErrorPage);
    }
  }, [windowWidth, showHeader]);

  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleScroll, handleResize]);

  useEffect(() => {
    const links = Array.from(document.querySelectorAll('.navigation-menu a'));
    const linkSectionMap = new Map<Element, Element>();

    links.forEach(link => {
      const target = link.getAttribute('href');
      if (target && target.startsWith('/#') && target.length > 2) {
        const section = document.querySelector(target.replace('/#', '#'));
        if (section) {
          linkSectionMap.set(link, section);
        }
      }
    });

    const onScroll = () => updateActiveLink(linkSectionMap);
    window.addEventListener('scroll', onScroll);
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [updateActiveLink, location]);

  const navbarClassNames = [
    'navbar-light',
    'navbar',
    styles.navbar,
    isSticky ? styles['is-sticky'] : ''
  ].join(' ');

  const brandClassNames = [
    styles['navbar-brand'],
    'navbar-brand',
    (!showHeader || isSticky) ? ['navbar-brand-show', styles['navbar-brand-show']].join(' ') : ''
  ].join(' ');

  const collapseClassNames = (showHeader && !isSticky)
    ? 'justify-content-center'
    : 'justify-content-start';

  return (
    <>
      <Container fluid className={styles.header} style={{ display: showHeader ? 'block' : 'none' }}>
        <Link to="/">
          <svg viewBox="0 0 131 50" className={styles["header-image"]}>
            <text className={styles["profissional-name"]}>Mayara Sena</text>
            <text className={styles["profissional-qualification"]}>nutricionista</text>
          </svg>
        </Link>
      </Container>

      <Navbar expand="lg" sticky={isSticky ? 'top' : undefined} className={navbarClassNames}>
        <Container fluid className={styles['container-fluid']}>
          <Link to="/index" className={brandClassNames}>
            <svg viewBox="0 0 131 50" className={styles['navbar-logo']}>
              <text className={styles['profissional-name']}>Mayara Sena</text>
              <text className={styles['profissional-qualification']}>nutricionista</text>
            </svg>
          </Link>

          <Navbar.Toggle aria-controls="navbarNavDropdown" />

          <Navbar.Collapse id="navbarNavDropdown" className={collapseClassNames}>
            <Nav className="navigation-menu">
              {menuItems.map((item, index) =>
                item.children ? (
                  <Nav.Item className={[styles['nav-item'], 'nav-item', 'dropdown'].join(' ')} key={index}>
                    <Nav.Link
                      href="#"
                      className={[styles['nav-link'], 'nav-link', 'dropdown-toggle', styles['dropdown-toggle']].join(' ')}
                      data-bs-toggle="dropdown"
                      role="button"
                      aria-haspopup="true"
                      ref={(el: HTMLAnchorElement | null) => { dropdownRefs.current[index] = el; }}
                      aria-expanded="false"
                      onClick={item.onClick}
                    >
                      {item.name}
                    </Nav.Link>
                    <ul className="dropdown-menu">
                      {item.children.map((child, cIndex) => (
                        <li className={child.subitem ? "dropdown-header px-3 py-2" : ""} key={cIndex}>
                          {child.subitem ? (
                            <div className="d-flex align-items-center">
                              {/* Foto à esquerda */}
                              {child.icon && (<img
                                src={child.icon}
                                alt="Foto do usuário"
                                className={styles['list-image']}
                              />)}

                              {/* Nome e botão sair à direita */}
                              <div className="ms-3 flex-grow-1">
                                <Link
                                  to={child.url ?? '#'}
                                  onClick={(e) => {
                                    if (!child.url) e.preventDefault();
                                    child.onClick?.();
                                    const toggle = dropdownRefs.current[index];
                                    if (toggle) {
                                      toggle.setAttribute('aria-expanded', 'false');
                                      const parentDropdown = toggle.closest('.dropdown');
                                      if (parentDropdown) {
                                        parentDropdown.classList.remove('show');
                                        const menu = parentDropdown.querySelector('.dropdown-menu');
                                        if (menu) menu.classList.remove('show');
                                      }
                                    }
                                  }}
                                  className={styles['dropdown-header-link']}
                                >
                                  {child.name}
                                </Link>
                                {child.subitem && (<div className="text-end">
                                  <button
                                    className={`btn btn-link p-0 text-danger text-decoration-none ${styles['dropdown-header-sublink']}`}
                                    onClick={() => {
                                      child.subitem?.onClick?.();
                                      const toggle = dropdownRefs.current[index];
                                      if (toggle) {
                                        toggle.setAttribute('aria-expanded', 'false');
                                        const parentDropdown = toggle.closest('.dropdown');
                                        if (parentDropdown) {
                                          parentDropdown.classList.remove('show');
                                          const menu = parentDropdown.querySelector('.dropdown-menu');
                                          if (menu) menu.classList.remove('show');
                                        }
                                      }
                                    }}
                                  >
                                    {child.subitem.name || 'Sair'}
                                  </button>
                                </div>)}
                              </div>
                            </div>) : (
                            <Link
                              to={child.url ?? '#'}
                              onClick={(e) => {
                                if (!child.url) e.preventDefault();
                                child.onClick?.();
                                const toggle = dropdownRefs.current[index];
                                if (toggle) {
                                  toggle.setAttribute('aria-expanded', 'false');
                                  const parentDropdown = toggle.closest('.dropdown');
                                  if (parentDropdown) {
                                    parentDropdown.classList.remove('show');
                                    const menu = parentDropdown.querySelector('.dropdown-menu');
                                    if (menu) menu.classList.remove('show');
                                  }
                                }
                              }}
                              className={['dropdown-item', activeLink === child.url ? styles['active'] : ''].join(' ')}
                            >
                              {child.name}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </Nav.Item>
                ) : (
                  <Nav.Item className={[styles['nav-item'], 'nav-item'].join(' ')} key={index}>
                    <Link
                      to={item.url ?? '#'}
                      onClick={(e) => {
                        if (!item.url) e.preventDefault();
                        item.onClick?.();
                      }}
                      className={[styles['nav-link'], 'nav-link', activeLink === item.url ? styles['active'] : ''].join(' ')}
                    >
                      {item.name}
                    </Link>
                  </Nav.Item>
                )
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </>
  );
}

export default NavbarComponent;
